// Dashboard: clips of your game (community's clip list), with reports and hides through community moderation.
// Community (wave 6) gives the game's creator the queue and log scoped to their game, and hide, unhide and dismiss
// on its clips; moderators also get remove. Rules: src/moderation.ts.

import { api, ApiError } from "../api.ts";
import type { Game } from "../catalogue.ts";
import { clipPath, clipVideo, fmtAgo, fmtMoment, reportButton, type Clip } from "../clips.ts";
import { clear, errorBox, h, section, toast } from "../dom.ts";
import { ACTION_DONE, allowed, canUnhide, hiddenTargets, modError, parseLog, parseQueue, reasonLabel, targetKey, titleFallback, titlesFromQueue, type Hidden, type LogEntry, type QueueItem, type Report, type Scope } from "../moderation.ts";
import { nameSpan } from "./game.ts";

/** Clip ids whose latest hide/unhide in the moderation log is a hide (newest entries first). */
export function hiddenClipIds(log: LogEntry[], gameId: string): string[] {
  return hiddenTargets(log, gameId, "clip").map((x) => x.target_id);
}

/** Open clip reports, by clip id. */
export function clipReports(items: QueueItem[]): Map<string, Report[]> {
  const m = new Map<string, Report[]>();
  for (const it of items) if (it.target_type === "clip") m.set(it.target_id, it.reports);
  return m;
}

type ClipAction = "hide" | "unhide" | "remove" | "dismiss";

export function gameClipsDash(g: Game): HTMLElement {
  const host = h("div");
  const load = () => clear(host, section(null, (body) => fill(body, g, load)));
  load();
  return h(
    "div",
    null,
    h("h1", null, `${g.title}: Clips`),
    h("p", { class: "dim" }, "Moments players saved in the Banger app and shared. Each one links back to your game with Play this."),
    host,
  );
}

async function fill(body: HTMLElement, g: Game, reload: () => void): Promise<void> {
  // the game's creator or a moderator? (the queue answers 403 not_moderator for everyone else)
  let reports: Map<string, Report[]> | null = null;
  let scope: Scope | null = null;
  try {
    const q = parseQueue(await api("community", `/moderation/queue?status=open&game=${encodeURIComponent(g.id)}`));
    reports = clipReports(q.items);
    scope = q.scope;
  } catch (e) {
    if (!(e instanceof ApiError && e.code === "not_moderator")) throw e;
  }
  const mod = async (action: ClipAction, clipId: string) => {
    if (action === "remove" && !confirm("Remove this clip for good? The video is deleted and this can't be undone.")) return;
    try {
      await api("community", "/moderation/actions", { body: { action, target_type: "clip", target_id: clipId } });
      toast({ hide: "Clip hidden", unhide: "Clip visible again", remove: "Clip removed", dismiss: ACTION_DONE.dismiss }[action]);
      reload();
    } catch (e) {
      const words = e instanceof ApiError ? modError(e.code) : null;
      toast(words ?? (e instanceof Error ? e.message : String(e)));
    }
  };

  // who hid what, from the game's moderation log (a creator can't unhide a moderator's hide)
  let hidden: Hidden[] = [];
  if (scope) {
    const log = await api("community", `/moderation/log?game=${encodeURIComponent(g.id)}&limit=500`).then(parseLog, () => [] as LogEntry[]);
    hidden = hiddenTargets(log, g.id, "clip");
  }
  const hiddenBy = new Map(hidden.map((x) => [x.target_id, x]));

  const list = h("ul", { class: "clip-grid" });
  const more = h("div", { class: "feed-more" });
  let count = 0;
  const page = async (cursor: string | null) => {
    const r = await api<{ clips: Clip[]; next_cursor: string | null }>("community", `/games/${encodeURIComponent(g.id)}/clips?limit=24${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`);
    for (const c of r.clips) list.append(dashClip(c, reports?.get(c.id) ?? [], scope, hiddenBy.get(c.id) ?? null, mod));
    count += r.clips.length;
    if (r.next_cursor) {
      const next = r.next_cursor;
      clear(more, h("button", { type: "button", class: "btn ghost", onclick: () => void page(next).catch((e) => clear(more, errorBox(e))) }, "Show more"));
    } else clear(more, count ? null : h("p", { class: "dim" }, "No clips of this game yet. Players save them in the Banger app (F8 or the overlay's Clip button)."));
  };
  await page(null);

  const reported = reports ? [...reports.entries()] : [];
  clear(
    body,
    scope
      ? h(
          "div",
          { class: "panel" },
          h("h2", null, "Reports"),
          reported.length
            ? h("p", null, `${reported.length} ${reported.length === 1 ? "clip has" : "clips have"} open reports. They're marked below; hide a clip to take it off your game's page and every feed.`)
            : h("p", { class: "dim" }, "No open reports on clips of this game."),
          scope === "creator" ? h("p", { class: "dim small" }, "You can hide clips of your game and dismiss reports. Removing a clip for good is for Banger moderators, and a clip a moderator hid stays hidden unless a moderator brings it back.") : null,
        )
      : h(
          "div",
          { class: "panel" },
          h("h2", null, "Reports"),
          h("p", null, "See something that shouldn't be here? Report it and the Banger moderators will look at it."),
        ),
    list,
    more,
    scope && hidden.length ? hiddenSection(hidden, reports ?? new Map(), scope, mod, g.id) : null,
  );
}

function dashClip(c: Clip, reports: Report[], scope: Scope | null, hid: Hidden | null, mod: (a: ClipAction, id: string) => void): HTMLElement {
  const can = scope ? allowed(scope, "clip") : [];
  const isHidden = c.status === "hidden" || hid !== null;
  const unhideOk = scope ? canUnhide(scope, { target_type: "clip", by: hid?.by ?? null }) : false;
  return h(
    "li",
    { class: `clip-card${reports.length ? " flagged" : ""}` },
    clipVideo(c),
    h(
      "div",
      { class: "clip-meta" },
      h("a", { href: clipPath(c.id), "data-link": true, class: "clip-title" }, c.title || "Untitled clip"),
      h("p", { class: "dim small" }, `at ${fmtMoment(c.moment_seconds)}`, c.version ? ` · v${c.version}` : null, ` · ${fmtAgo(c.created_at)} · by `, nameSpan(c.uploader_id)),
      isHidden ? h("p", null, h("span", { class: "badge warn" }, hid?.by === "moderator" ? "Hidden by a moderator" : "Hidden")) : null,
      reports.length ? reportList(reports) : null,
      h(
        "div",
        { class: "clip-actions" },
        scope
          ? [
              isHidden
                ? can.includes("unhide") && unhideOk
                  ? h("button", { type: "button", class: "btn small", onclick: () => mod("unhide", c.id) }, "Unhide")
                  : null
                : can.includes("hide")
                  ? h("button", { type: "button", class: "btn small", onclick: () => mod("hide", c.id) }, "Hide")
                  : null,
              reports.length && can.includes("dismiss") ? h("button", { type: "button", class: "btn small ghost", onclick: () => mod("dismiss", c.id) }, "Dismiss reports") : null,
              can.includes("remove") ? h("button", { type: "button", class: "btn small danger", onclick: () => mod("remove", c.id) }, "Remove") : null,
            ]
          : reportButton(c),
      ),
    ),
  );
}

function reportList(reports: Report[]): HTMLElement {
  const reasons = [...new Set(reports.map((r) => reasonLabel(r.reason)))].join(", ");
  return h(
    "details",
    { class: "small" },
    h("summary", null, h("span", { class: "badge bad" }, `${reports.length} ${reports.length === 1 ? "report" : "reports"}`), ` ${reasons}`),
    h("ul", { class: "plain" }, reports.map((r) => h("li", { class: "dim" }, `${reasonLabel(r.reason)}${r.note ? `: ${r.note}` : ""} · ${fmtAgo(r.created_at)}`))),
  );
}

/** Hidden clips (gone from the public list): each loaded by id. If community won't show one to this viewer, it says so. */
function hiddenSection(hidden: Hidden[], reports: Map<string, Report[]>, scope: Scope, mod: (a: ClipAction, id: string) => void, gameId: string): HTMLElement {
  const list = h("ul", { class: "clip-grid" });
  // a clip this viewer can't open still gets its title, from the actioned queue's preview (never its id)
  let titles: Promise<Map<string, string>> | null = null;
  const titleOf = (id: string) =>
    (titles ??= api("community", `/moderation/queue?status=actioned&game=${encodeURIComponent(gameId)}`).then((r) => titlesFromQueue(parseQueue(r).items), () => new Map<string, string>())).then(
      (m) => m.get(targetKey("clip", id)) ?? titleFallback("clip"),
    );
  for (const x of hidden.slice(0, 24)) {
    const slot = h("li", { class: "clip-card" }, h("p", { class: "dim", style: { padding: "12px" } }, "Loading…"));
    list.append(slot);
    api<{ clip: Clip }>("community", `/clips/${encodeURIComponent(x.target_id)}`)
      .then((r) => slot.replaceWith(dashClip(r.clip, reports.get(x.target_id) ?? [], scope, x, mod)))
      .catch(async () =>
        clear(
          slot,
          h(
            "div",
            { class: "clip-meta" },
            h("p", { class: "clip-title" }, await titleOf(x.target_id)),
            h("p", { class: "small" }, h("span", { class: "badge warn" }, x.by === "moderator" ? "Hidden by a moderator" : "Hidden")),
            h("p", { class: "dim small" }, "This clip can't be shown here."),
            canUnhide(scope, { target_type: "clip", by: x.by }) ? h("button", { type: "button", class: "btn small", onclick: () => mod("unhide", x.target_id) }, "Unhide") : null,
          ),
        ),
      );
  }
  return h("section", { class: "section" }, h("h2", null, "Hidden clips"), h("p", { class: "dim small" }, "Off your game's page and every feed. Only you, the moderators and the person who shared each clip can see them."), list);
}
